import { useMemo } from 'react';
import { useGetKanbanPages } from './useGetKanbanPages';
import { useGetKanbanCategories } from './useGetKanbanCategories';
import { useGetTodoTasks } from './useGetTodoTasks';
import { KanbanPage } from '../interfaces/kanbanPage.interface';
import { KanbanCategory } from '../interfaces/kanbanCategory.interface';
import { FlowTask } from '../interfaces/flowTask.interface';

export interface KanbanDataCategory extends KanbanCategory {
    tasks: FlowTask[];
}

export interface KanbanDataPage extends KanbanPage {
    categories: KanbanDataCategory[];
}

export interface KanbanData {
    pages: KanbanDataPage[];
    isLoading: boolean;
    isError: boolean;
    errors: unknown[];
}

type TaskMap = Record<string, FlowTask[]>;
type CategoryMap = Record<string, KanbanDataCategory[]>;

const byOrder = <T extends { order?: number }>(a: T, b: T) => (a.order || 0) - (b.order || 0);

const groupTasksByCategory = (tasks: FlowTask[]): TaskMap =>
    tasks.reduce<TaskMap>((acc, task) => {
        const key = task.kanbanCategoryUid;
        (acc[key] ??= []).push(task);
        return acc;
    }, {});

const groupCategoriesByPage = (categories: KanbanCategory[], tasksByCategory: TaskMap): CategoryMap =>
    categories.reduce<CategoryMap>((acc, category) => {
        const key = category.kanbanPageUid;

        const categoryWithTasks: KanbanDataCategory = {
            ...category,
            tasks: tasksByCategory[category.uid] || [],
        };

        (acc[key] ??= []).push(categoryWithTasks);
        return acc;
    }, {});

export const useKanbanData = (): KanbanData => {
    const pagesQuery = useGetKanbanPages();
    const categoriesQuery = useGetKanbanCategories();
    const tasksQuery = useGetTodoTasks();

    const isLoading = pagesQuery.isLoading || categoriesQuery.isLoading || tasksQuery.isLoading;
    const isError = pagesQuery.isError || categoriesQuery.isError || tasksQuery.isError;
    const errors = [pagesQuery.error, categoriesQuery.error, tasksQuery.error].filter(Boolean);

    const pages = useMemo<KanbanDataPage[]>(() => {
        if (isLoading || isError) return [];

        const rawPages = pagesQuery.data ?? [];
        const rawCategories = categoriesQuery.data ?? [];
        const rawTasks = tasksQuery.data ?? [];

        const tasksByCategory = groupTasksByCategory(rawTasks);
        const categoriesByPage = groupCategoriesByPage(rawCategories, tasksByCategory);

        // Sort categories within each page
        Object.values(categoriesByPage).forEach(list => list.sort(byOrder));

        // console.log('categoriesByPage keys:', Object.keys(categoriesByPage));
        // console.log('page.uids:', rawPages.map(p => p.uid));

        // Attach categories to pages
        const structuredPages: KanbanDataPage[] = rawPages.map(page => ({
            ...page,
            categories: categoriesByPage[page.uid] ?? [],
        }));

        // Sort pages
        structuredPages.sort(byOrder);

        return structuredPages;
    }, [
        pagesQuery.data,
        categoriesQuery.data,
        tasksQuery.data,
        isLoading,
        isError,
    ]);

    return { pages, isLoading, isError, errors };
};
